// src/utils/apiClient.js
import axios from "axios";
import { getBaseUrl } from "./getBaseUrl"; // Make sure this returns your base URL

const apiClient = axios.create({
  baseURL: getBaseUrl(),
  headers: {
    "Content-Type": "application/json",
  },
});

apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // For custom domains (lms.abcinstitute.com), the Origin header already
    // carries the domain. But for extra safety, if we're on a non-lmscampus
    // hostname, send it explicitly so the backend tenant resolver can use it.
    const hostname = window.location.hostname;
    if (
      hostname !== "localhost" &&
      !hostname.endsWith("lmscampus.com") &&
      !hostname.endsWith("visiblecampus.com")
    ) {
      config.headers["X-Custom-Domain"] = hostname;
    }

    // Attach selected branch (if any) for branch-scoped queries
    const branchId = localStorage.getItem('vc-selected-branch');
    if (branchId && branchId !== 'all') {
      config.headers["X-Branch-Id"] = branchId;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

apiClient.interceptors.response.use(
  (response) => {
    // ── Grace period detection from response headers ──
    // The backend sets X-Plan-Grace headers when trial/subscription expired
    // but the institute is still within the 7-day grace window.
    const isGrace = response.headers?.['x-plan-grace'] === 'true';
    if (isGrace) {
      const graceDays = parseInt(response.headers['x-plan-grace-days'] || '0', 10);
      const graceStatus = response.headers['x-plan-status'] || 'expired';
      // Dispatch a custom event so PlanContext / dashboard can pick it up
      window.dispatchEvent(new CustomEvent('vc-plan-grace', {
        detail: { inGrace: true, daysLeft: graceDays, status: graceStatus },
      }));
    }
    return response;
  },
  (error) => {
    const status = error.response?.status;
    const code = error.response?.data?.code;

    if (status === 401) {
      const isSessionReplaced = code === 'SESSION_REPLACED';
      // Token expired or invalid — clear auth & redirect
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('selectedRole');
      window.dispatchEvent(new Event('vc-auth-change'));
      if (isSessionReplaced) {
        sessionStorage.setItem('logoutReason', error.response?.data?.message || 'You have been logged out because you logged in from another device or tab.');
      }
      if (!window.location.pathname.startsWith('/login')) {
        window.location.href = '/login';
      }
    }

    // ── Plan / Trial expired (grace period over) ──
    if (status === 403 && (code === 'TRIAL_EXPIRED' || code === 'PLAN_EXPIRED')) {
      // Dispatch event so the app can show a full-screen upgrade prompt
      window.dispatchEvent(new CustomEvent('vc-plan-expired', {
        detail: { code, message: error.response?.data?.message },
      }));
      // Redirect to billing page if not already there
      if (!window.location.pathname.startsWith('/billing')) {
        window.location.href = '/billing';
      }
    }

    // ── Institute suspended ──
    if (status === 403 && code === 'INSTITUTE_SUSPENDED') {
      window.dispatchEvent(new CustomEvent('vc-institute-suspended', {
        detail: { message: error.response?.data?.message },
      }));
    }

    // ── Plan limit or no-plan reached (planGate middleware) ──
    // Only show toast for numeric limits (limitField present), NOT for
    // feature-toggle rejections (featureField present) — those are handled
    // silently by PlanGate / LockedFeaturePage.
    const data = error.response?.data || {};
    if (status === 403 && (data.planLimit || data.noPlan) && !data.featureField) {
      window.dispatchEvent(new CustomEvent('vc-plan-limit', {
        detail: {
          message: data.message || 'You have reached a plan limit.',
          limitField: data.limitField,
          current: data.current,
          max: data.max,
          noPlan: !!data.noPlan,
        },
      }));
    }

    return Promise.reject(error);
  }
);

export default apiClient;
